import type { UnknownAction } from '@reduxjs/toolkit'

import {
  defineListener,
  errorView,
  resourceListTag,
  resourceScopeCleared,
  resourceTag,
  userScopeCleared,
  type ModuleListener,
  type ModuleListenerApi,
} from '@krp/client-runtime'

import { AQSI_TAGS } from '../../../shared/api/tags'
import { sameId } from '../../../shared/api/wire'
import { storeDispatch, type AqsiDispatch } from '../../../shared/model/dispatch'
import { feedbackOfError, type CommandFeedback } from '../../../shared/model/feedback'
import { FEEDBACK_SCOPES, feedbackSet } from '../../../shared/model/feedbackSlice'
import { newCommandIntent } from '../../../shared/model/ids'
import { ridKey } from '../../../shared/model/keys'
import { AQSI_RECEIPT_TAG, aqsiReceipt } from '../../receipts/api/receiptsApi'
import { aqsiMySession } from '../../rmk/api/rmkApi'
import {
  aqsiOperationResolve,
  aqsiSaleOperation,
  aqsiSaleReconcile,
  saleApi,
} from '../../sale/api/saleApi'
import { NOT_ARRIVED_AFTER_MS } from '@krp/sale-ui'
import type { AqsiSaleOperation } from '../../sale/model/types'
import {
  aqsiRefund,
  aqsiRefundCancel,
  aqsiRefundCreate,
  aqsiRefundFiscalize,
  aqsiRefundPay,
  aqsiRefundSeat,
  refundsApi,
  type RefundCommandArgs,
  type RefundSeatArgs,
} from '../api/refundsApi'
import {
  refundCancelRequested,
  refundCreateRequested,
  refundFiscalizeRequested,
  refundPayRequested,
  refundReconcileRequested,
  refundReloadRequested,
  refundStatusCheckRequested,
} from './commands'
import {
  blocksRefundCommands,
  refundCommandNotArrived,
  refundCommandOf,
  refundCommandSettled,
  refundCommandStarted,
  refundCommandUnknown,
} from './refundCommandsSlice'
import { refundDraftCleared, refundFinishedShown } from './refundsSlice'
import type { AqsiRefund, RefundCommand, RefundCommandKind, RefundCommandsState, RefundsIntentState, SeatActive } from './types'

export interface RefundModuleSlices {
  refunds: RefundsIntentState
  commands: RefundCommandsState
}

const refundFeedbackKey = (resourceId: string, receiptId: string) => ridKey(resourceId, FEEDBACK_SCOPES.refund(receiptId))

function note(command: string, target: string, phase: CommandFeedback['phase'], text: string | null): CommandFeedback {
  return { command, target, phase, error: null, note: text }
}

/** Возвращённое по чеку изменилось (или могло): журнал и детализация чека перечитываются. */
function rereadReceipt(dispatch: AqsiDispatch, resourceId: string, receiptId: string): void {
  dispatch(
    refundsApi.util.invalidateTags([
      resourceTag(AQSI_RECEIPT_TAG, resourceId, receiptId),
      resourceTag(AQSI_RECEIPT_TAG, resourceId, 'LIST'),
    ]),
  )
}

/**
 * Ответ команды — в кэш RTK Query, а не в slice. Завершённый возврат больше не
 * занимает кассу (служба держит одну незавершённую продажу/возврат на кассу):
 * касса становится пустой, а сам возврат остаётся для итога.
 */
function showRefund(dispatch: AqsiDispatch, seat: RefundSeatArgs, refund: AqsiRefund, shown: Set<string>): void {
  if (!refund.isFinished) {
    void dispatch(refundsApi.util.upsertQueryData('aqsiRefundSeat', seat, { kind: 'refund', refund }))
    return
  }
  void dispatch(refundsApi.util.upsertQueryData('aqsiRefund', { resourceId: seat.resourceId, refundId: refund.id }, refund))
  void dispatch(refundsApi.util.upsertQueryData('aqsiRefundSeat', seat, { kind: 'none' }))
  shown.add(ridKey(seat.resourceId, refund.id.toLowerCase()))
  dispatch(refundFinishedShown({ resourceId: seat.resourceId, receiptId: refund.sourceReceiptId, refundId: refund.id }))
}

/** Касса уже знает нашу команду: служба записала её операцию (наш UUID), создала или завершила возврат. */
function seatKnowsCommand(seat: SeatActive, command: RefundCommand): 'known' | 'gone' | 'unknown' {
  if (command.kind === 'create') {
    if (seat.kind === 'refund' && seat.refund.sourceReceiptId === command.receiptId) return 'known'
    // На кассе другая продажа или другой возврат: служба держит один — наш создан не был (или уже завершён).
    return seat.kind === 'none' ? 'unknown' : 'gone'
  }
  if (seat.kind !== 'refund' || command.refundId === null || !sameId(seat.refund.id, command.refundId)) return 'gone'
  const refund = seat.refund
  const id = command.operationId
  switch (command.kind) {
    case 'pay':
      return (refund.purchaseOperationId !== null && sameId(refund.purchaseOperationId, id)) ||
        (refund.receiptOperationId !== null && sameId(refund.receiptOperationId, id))
        ? 'known'
        : 'unknown'
    case 'fiscalize':
      return refund.receiptOperationId !== null && sameId(refund.receiptOperationId, id) ? 'known' : 'unknown'
    case 'cancel':
      // Отменённый возврат кассу не занимает: раз он на кассе — отмена до службы не дошла (пока).
      return 'unknown'
  }
}

const COMMAND_TEXT: Readonly<Record<RefundCommandKind, { sending: string; done: string; unknown: string }>> = {
  create: {
    sending: 'Возврат оформляется…',
    done: 'Возврат оформлен — верните деньги покупателю',
    unknown:
      'Ответ на «Вернуть» не получен: неизвестно, оформлен ли возврат. Повтор запрещён — касса перечитана, смотрите её состояние или нажмите «Проверить статус».',
  },
  pay: {
    sending: 'Деньги возвращаются…',
    done: 'Команда возврата денег отправлена — ход виден ниже',
    unknown:
      'Ответ на возврат денег не получен: неизвестно, вернулись ли деньги. Повтор запрещён — возврат перечитан, смотрите его состояние.',
  },
  fiscalize: {
    sending: 'Чек возврата отправляется…',
    done: 'Чек возврата отправлен — ход виден ниже',
    unknown: 'Ответ на чек возврата не получен: неизвестно, пробит ли он. Повтор запрещён — возврат перечитан.',
  },
  cancel: {
    sending: 'Возврат отменяется…',
    done: 'Возврат отменён: деньги не двигались, количество чека освобождено',
    unknown: 'Ответ на отмену не получен. Отмена не повторяется — возврат перечитан, проверьте, отменён ли он.',
  },
}

const COMMAND_ENDPOINTS = {
  pay: aqsiRefundPay,
  fiscalize: aqsiRefundFiscalize,
  cancel: aqsiRefundCancel,
} as const

const BLOCKED_TEXT =
  'Прошлая команда на этой кассе ещё не завершена или её исход неизвестен. Проверьте статус — повторная команда запрещена.'

/**
 * Listeners окна возврата. `select` — где в состоянии модуля лежат намерение
 * возврата и ждущие команды (модуль собирает свои слайсы сам).
 */
export function createRefundListeners<S>(select: (state: S) => RefundModuleSlices): ModuleListener<S>[] {
  /** Сверки в полёте: второе нажатие не шлёт вторую на ту же операцию. */
  const reconciling = new Set<string>()
  /**
   * Эпоха области: выход пользователя и сброс rid её сдвигают. Ответ, пришедший
   * после сброса, не пишет в кэш и намерение ушедшей области (G4).
   */
  let userEpoch = 0
  const resourceEpochs = new Map<string, number>()
  /** Эпоха области на старте чтения статуса: ответ, начатый до сброса, не пишет намерение ушедшей области. */
  const requestEpochs = new Map<string, string>()
  /** Итог возврата показан один раз: повторное чтение статуса не возвращает его поверх «Ещё возврат». */
  const finishedShown = new Set<string>()
  const epochOf = (resourceId: string) => `${userEpoch}:${resourceEpochs.get(resourceId.toLowerCase()) ?? 0}`

  /** Опасная команда уже идёт или её исход неизвестен: новую не отдаём, баннер «отправляется» не перекрываем. */
  function refuseWhilePending(api: ModuleListenerApi<S>, seat: RefundSeatArgs, receiptId: string, command: string): boolean {
    const pending = refundCommandOf(select(api.getModuleState()).commands, seat)
    if (!blocksRefundCommands(pending)) return false
    if (pending?.phase !== 'sending') {
      api.dispatch(
        feedbackSet({
          key: refundFeedbackKey(seat.resourceId, receiptId),
          feedback: note(command, receiptId, 'rejected', BLOCKED_TEXT),
        }),
      )
    }
    return true
  }

  async function runRefundCommand(
    kind: Exclude<RefundCommandKind, 'create'>,
    payload: RefundSeatArgs & { receiptId: string; refundId: string; expectedVersion: number },
    api: ModuleListenerApi<S>,
  ): Promise<void> {
    const dispatch = storeDispatch(api.dispatch)
    const seat = { resourceId: payload.resourceId, registerKey: payload.registerKey, deviceId: payload.deviceId }
    const key = refundFeedbackKey(seat.resourceId, payload.receiptId)
    const command = `refund.${kind}`
    // Двойной клик и неизвестный исход прошлой команды не шлют второй POST (ADR-11).
    if (refuseWhilePending(api, seat, payload.receiptId, command)) return
    const intent = newCommandIntent()
    const commandRef = { ...seat, operationId: intent.operationId }
    dispatch(
      refundCommandStarted({
        ...seat,
        kind,
        receiptId: payload.receiptId,
        refundId: payload.refundId,
        version: payload.expectedVersion,
        operationId: intent.operationId,
        startedAt: new Date().toISOString(),
        phase: 'sending',
      }),
    )
    dispatch(feedbackSet({ key, feedback: note(command, payload.receiptId, 'sending', COMMAND_TEXT[kind].sending) }))
    const args: RefundCommandArgs = { ...seat, refundId: payload.refundId, intent, expectedVersion: payload.expectedVersion }
    const epoch = epochOf(seat.resourceId)
    try {
      const refund = await dispatch(COMMAND_ENDPOINTS[kind].initiate(args, { track: false })).unwrap()
      if (epochOf(seat.resourceId) !== epoch) return
      dispatch(refundCommandSettled(commandRef))
      showRefund(dispatch, seat, refund, finishedShown)
      rereadReceipt(dispatch, seat.resourceId, payload.receiptId)
      dispatch(feedbackSet({ key, feedback: note(command, payload.receiptId, 'done', COMMAND_TEXT[kind].done) }))
    } catch (error) {
      if (epochOf(seat.resourceId) !== epoch) return
      const result = feedbackOfError(command, payload.receiptId, error)
      if (result.phase === 'unknown') {
        // Не повторять: кассу перечитывает endpoint (invalidatesTags), исход скажет чтение.
        dispatch(refundCommandUnknown(commandRef))
        dispatch(feedbackSet({ key, feedback: { ...result, note: COMMAND_TEXT[kind].unknown } }))
      } else {
        dispatch(refundCommandSettled(commandRef))
        dispatch(feedbackSet({ key, feedback: result }))
      }
    }
  }

  return [
    {
      predicate: (action: UnknownAction) => userScopeCleared.match(action) || resourceScopeCleared.match(action),
      effect: (action: UnknownAction) => {
        if (resourceScopeCleared.match(action)) {
          const rid = action.payload.resourceId.toLowerCase()
          resourceEpochs.set(rid, (resourceEpochs.get(rid) ?? 0) + 1)
        } else {
          userEpoch += 1
          finishedShown.clear()
        }
      },
    },

    defineListener<S, ReturnType<typeof refundCreateRequested>>(refundCreateRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { receiptId, lines } = action.payload
      // Аргументы кэша кассы — ровно `{resourceId, registerKey, deviceId}`: лишнее поле дало бы другую запись кэша.
      const seat = { resourceId: action.payload.resourceId, registerKey: action.payload.registerKey, deviceId: action.payload.deviceId }
      const key = refundFeedbackKey(seat.resourceId, receiptId)
      if (refuseWhilePending(api, seat, receiptId, 'refund.create')) return
      const start = (expectedVersion: number) => {
        const intent = newCommandIntent()
        dispatch(
          refundCommandStarted({
            ...seat,
            kind: 'create',
            receiptId,
            refundId: null,
            version: expectedVersion,
            operationId: intent.operationId,
            startedAt: new Date().toISOString(),
            phase: 'sending',
          }),
        )
        return intent
      }
      let intent = start(action.payload.expectedVersion)
      dispatch(feedbackSet({ key, feedback: note('refund.create', receiptId, 'sending', COMMAND_TEXT.create.sending) }))
      const epoch = epochOf(seat.resourceId)
      const send = (expectedVersion: number) =>
        dispatch(
          aqsiRefundCreate.initiate({ ...seat, receiptId, lines, intent, expectedVersion }, { track: false }),
        ).unwrap()
      try {
        let refund: AqsiRefund
        try {
          refund = await send(action.payload.expectedVersion)
        } catch (error) {
          // Отказ по версии сессии эффекта не имел: перечитать кассы и создать ещё раз (новый ключ) — один раз.
          if (errorView(error).code !== 'version-mismatch' || epochOf(seat.resourceId) !== epoch) throw error
          const own = await dispatch(aqsiMySession.initiate({ resourceId: seat.resourceId }, { subscribe: false, forceRefetch: true }))
          // Пока перечитывались кассы, область могли сбросить (выход, смена ресурса): новой команды не будет.
          if (epochOf(seat.resourceId) !== epoch) return
          const mine = own.data
          if (mine == null || !sameId(mine.registerKey, seat.registerKey)) throw error
          dispatch(refundCommandSettled({ ...seat, operationId: intent.operationId }))
          intent = start(mine.version)
          refund = await send(mine.version)
        }
        if (epochOf(seat.resourceId) !== epoch) return
        dispatch(refundCommandSettled({ ...seat, operationId: intent.operationId }))
        dispatch(refundDraftCleared({ resourceId: seat.resourceId, receiptId }))
        showRefund(dispatch, seat, refund, finishedShown)
        rereadReceipt(dispatch, seat.resourceId, receiptId)
        dispatch(feedbackSet({ key, feedback: note('refund.create', receiptId, 'done', COMMAND_TEXT.create.done) }))
      } catch (error) {
        if (epochOf(seat.resourceId) !== epoch) return
        const commandRef = { ...seat, operationId: intent.operationId }
        const result = feedbackOfError('refund.create', receiptId, error)
        if (result.phase === 'unknown') {
          dispatch(refundCommandUnknown(commandRef))
          dispatch(feedbackSet({ key, feedback: { ...result, note: COMMAND_TEXT.create.unknown } }))
        } else {
          // Отказ службы — дословно (больше проданного, чек не подтверждён, смешанная оплата…).
          dispatch(refundCommandSettled(commandRef))
          dispatch(feedbackSet({ key, feedback: result }))
        }
      }
    }),

    defineListener<S, ReturnType<typeof refundPayRequested>>(refundPayRequested.match, (action, api) =>
      runRefundCommand('pay', action.payload, api),
    ),
    defineListener<S, ReturnType<typeof refundFiscalizeRequested>>(refundFiscalizeRequested.match, (action, api) =>
      runRefundCommand('fiscalize', action.payload, api),
    ),
    defineListener<S, ReturnType<typeof refundCancelRequested>>(refundCancelRequested.match, (action, api) =>
      runRefundCommand('cancel', action.payload, api),
    ),

    // «Проверить статус» — только чтения: касса места, операция возврата, сам чек.
    defineListener<S, ReturnType<typeof refundStatusCheckRequested>>(
      refundStatusCheckRequested.match,
      async (action, api) => {
        const dispatch = storeDispatch(api.dispatch)
        const { resourceId, registerKey, deviceId, receiptId, operationId } = action.payload
        const reads: Promise<unknown>[] = [
          dispatch(aqsiRefundSeat.initiate({ resourceId, registerKey, deviceId }, { subscribe: false, forceRefetch: true })),
          dispatch(aqsiReceipt.initiate({ resourceId, receiptId }, { subscribe: false, forceRefetch: true })),
        ]
        if (operationId !== null) {
          reads.push(dispatch(aqsiSaleOperation.initiate({ resourceId, operationId }, { subscribe: false, forceRefetch: true })))
        }
        await Promise.all(reads)
      },
    ),

    defineListener<S, ReturnType<typeof refundReconcileRequested>>(refundReconcileRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { resourceId, receiptId, operationId, expectedVersion } = action.payload
      const flight = ridKey(resourceId, operationId.toLowerCase())
      if (reconciling.has(flight)) return
      reconciling.add(flight)
      const key = refundFeedbackKey(resourceId, receiptId)
      dispatch(feedbackSet({ key, feedback: note('refund.reconcile', receiptId, 'sending', null) }))
      const epoch = epochOf(resourceId)
      try {
        const status = await dispatch(
          aqsiSaleReconcile.initiate({ resourceId, operationId, expectedVersion, intent: newCommandIntent() }, { track: false }),
        ).unwrap()
        if (epochOf(resourceId) !== epoch) return
        void dispatch(saleApi.util.upsertQueryData('aqsiSaleOperation', { resourceId, operationId }, status))
        dispatch(feedbackSet({ key, feedback: note('refund.reconcile', receiptId, 'done', 'Сверка с кабинетом выполнена') }))
      } catch (error) {
        if (epochOf(resourceId) !== epoch) return
        // Сверка только читает кабинет: её отказ — дословно; операцию перечитывает endpoint.
        dispatch(feedbackSet({ key, feedback: feedbackOfError('refund.reconcile', receiptId, error) }))
      } finally {
        reconciling.delete(flight)
      }
    }),

    // Эпоха на старте чтения/сверки статуса — чтобы опознать ответ, начатый до сброса области.
    {
      predicate: (action: UnknownAction) =>
        aqsiSaleOperation.matchPending(action) ||
        aqsiSaleReconcile.matchPending(action) ||
        aqsiOperationResolve.matchPending(action) ||
        aqsiSaleOperation.matchRejected(action) ||
        aqsiSaleReconcile.matchRejected(action) ||
        aqsiOperationResolve.matchRejected(action),
      effect: (action: UnknownAction) => {
        if (aqsiSaleOperation.matchPending(action) || aqsiSaleReconcile.matchPending(action) || aqsiOperationResolve.matchPending(action)) {
          if (requestEpochs.size > 200) requestEpochs.clear()
          requestEpochs.set(action.meta.requestId, epochOf(action.meta.arg.originalArgs.resourceId))
        } else if (
          aqsiSaleOperation.matchRejected(action) ||
          aqsiSaleReconcile.matchRejected(action) ||
          aqsiOperationResolve.matchRejected(action)
        ) {
          requestEpochs.delete(action.meta.requestId)
        }
      },
    },

    // Чек возврата пробит (ответом, сверкой или вручную): показать итог возврата этого чека.
    {
      predicate: (action: UnknownAction) =>
        aqsiSaleOperation.matchFulfilled(action) ||
        aqsiSaleReconcile.matchFulfilled(action) ||
        aqsiOperationResolve.matchFulfilled(action),
      effect: async (action: UnknownAction, api: ModuleListenerApi<S>) => {
        let status: AqsiSaleOperation
        let resourceId: string
        if (aqsiSaleOperation.matchFulfilled(action)) {
          status = action.payload
          resourceId = action.meta.arg.originalArgs.resourceId
        } else if (aqsiSaleReconcile.matchFulfilled(action) || aqsiOperationResolve.matchFulfilled(action)) {
          status = action.payload
          resourceId = action.meta.arg.originalArgs.resourceId
        } else {
          return
        }
        const startedIn = requestEpochs.get(action.meta.requestId)
        requestEpochs.delete(action.meta.requestId)
        if (status.kind !== 'refundReceipt' || status.state !== 'Succeeded') return
        // Чтение, начатое до сброса области, и уже показанный итог не трогают намерение.
        const shownKey = ridKey(resourceId, status.saleId.toLowerCase())
        if (startedIn !== epochOf(resourceId) || finishedShown.has(shownKey)) return
        finishedShown.add(shownKey)
        const dispatch = storeDispatch(api.dispatch)
        const epoch = epochOf(resourceId)
        const result = await dispatch(
          aqsiRefund.initiate({ resourceId, refundId: status.saleId }, { subscribe: false, forceRefetch: true }),
        )
        const refund = result.data
        if (refund === undefined || epochOf(resourceId) !== epoch) return
        dispatch(refundFinishedShown({ resourceId, receiptId: refund.sourceReceiptId, refundId: refund.id }))
        rereadReceipt(dispatch, resourceId, refund.sourceReceiptId)
      },
    },

    // Чтение кассы — единственный способ узнать исход команды без ответа: дошла ли она до службы.
    {
      predicate: (action: UnknownAction) => aqsiRefundSeat.matchFulfilled(action),
      effect: (action: UnknownAction, api: ModuleListenerApi<S>) => {
        if (!aqsiRefundSeat.matchFulfilled(action)) return
        const seat = action.meta.arg.originalArgs
        const command = refundCommandOf(select(api.getModuleState()).commands, seat)
        if (command === undefined || command.phase === 'sending') return
        const commandRef = { ...seat, operationId: command.operationId }
        const known = seatKnowsCommand(action.payload, command)
        if (known !== 'unknown') {
          // Касса несёт нашу операцию или уже другое: дальше правду говорит она сама.
          api.dispatch(refundCommandSettled(commandRef))
          return
        }
        if (command.phase === 'not-arrived') return
        const started = Date.parse(command.startedAt)
        const readAt = action.meta.fulfilledTimeStamp
        if (Number.isFinite(started) && readAt - started >= NOT_ARRIVED_AFTER_MS) {
          api.dispatch(refundCommandNotArrived(commandRef))
        }
      },
    },

    defineListener<S, ReturnType<typeof refundReloadRequested>>(refundReloadRequested.match, (action, api) => {
      const { resourceId, receiptId } = action.payload
      const dispatch = storeDispatch(api.dispatch)
      dispatch(
        refundsApi.util.invalidateTags([
          resourceListTag(AQSI_TAGS.saleActive, resourceId),
          resourceListTag(AQSI_TAGS.registers, resourceId),
        ]),
      )
      rereadReceipt(dispatch, resourceId, receiptId)
    }),
  ]
}
